<template>
  <v-row>
    <v-expansion-panels elevation="0">
      <v-expansion-panel>
        <v-expansion-panel-title>{{ label }}</v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row>
            <v-col
              v-for="field in fields"
              :key="field.value || field.label"
              :cols="`${field.col || 12}`"
            >
              <component
                v-if="!field.children"
                :is="field.component"
                :model-value="getValue(field)"
                @update:model-value="val => updateValue(field, val)"
                v-bind="getFieldProps(field)"
                :rules="field.required ? [v => !!v || 'Campo obligatorio'] : []"
                variant="solo-filled"
              />

              <component
                v-else
                :is="field.component"
                :model-value="getValue(field)"
                @update:model-value="val => updateValue(field, val)"
                :fields="field.children"
                :summary="field.summary"
                :label="field.label"
                :form="form"
                :layout-only="field.layoutOnly"
                variant="solo-filled"
              />
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-row>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import type { PropType } from 'vue';
import type { FormField } from '@/types/form';
import { remoteOptionsRegistry } from '@/lib/remoteOptions';

const props = defineProps({
  fields: Array as PropType<FormField[]>,
  modelValue: Object as PropType<Record<string, any>>,
  form: Object as PropType<Record<string, any>>,
  layoutOnly: { type: Boolean, default: false },
  label: { type: String, default: 'Grupo de Campos' },
});

const emit = defineEmits(['update:modelValue']);

const baseModel = computed(() => {
  const target = props.layoutOnly ? props.form : props.modelValue;
  return target && typeof target === 'object' ? target : {};
});

const getValue = (field: FormField) => {
  const model = baseModel.value as Record<string, any>;
  return model?.[field.value];
};

const updateValue = (field: FormField, val: any) => {
  const model = baseModel.value as Record<string, any>;
  const updated = { ...model, [field.value]: val };

  // En modo layoutOnly actualiza directamente el form raíz
  if (props.layoutOnly && props.form) {
    props.form[field.value] = val;
  }

  emit('update:modelValue', updated);
};

// Agrega mapeo inicial de valores planos a objetos
onMounted(async () => {
  for (const field of props.fields || []) {
    if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
      const fetchFn = remoteOptionsRegistry[field.fetchOptions];
      const options = await fetchFn('');
      field.options = options;

      const val = (baseModel.value as Record<string, any>)?.[field.value];
      const itemValueKey = field.itemValue || 'value';

      if (val && typeof val !== 'object') {
        const match = options.find(opt => opt[itemValueKey] === val);
        if (match) {
          emit('update:modelValue', {
            ...(baseModel.value as Record<string, any>),
            [field.value]: match,
          });
        }
      }
    }
  }
});

const getFieldProps = (field: FormField) => {
  const props: Record<string, any> = {
    label: field.label,
    placeholder: field.placeholder ?? '',
    clearable: true,
    density: 'compact',
    type: field.type || 'text',
    prefix: field.prefix,
    suffix: field.suffix,
    'prepend-inner-icon': field.icon,
    disabled: field.permission === 'disabled',
    readonly: field.readonly,
    'item-title': field.itemText || 'label',
    'item-value': field.itemValue || 'value',
  };

  if (field.options) {
    props.items = field.options;
  }

  if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
    props['onUpdate:search'] = async (val: string) => {
      if (!val || val.length < 2) return;
      const fetchFn = remoteOptionsRegistry[field.fetchOptions!];
      const results = await fetchFn(val);
      field.options = results;
      props.items = results;
    };
  }

  return props;
};
</script>
